import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/system";
import { Typography } from "@mui/material";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Button from "react-bootstrap/Button";
import { colors } from "../config/colors";
import { SideBarAddLogo, BackButtonLogo } from "../assets/icons";
import {
  DISPLAY_FLEX,
  FLEX_JUSTIFY_SPACE_BETWEEN,
  TEXT_DECORATION_NONE,
  FLEX_ALIGN_CENTER,
  FLEX_DIRECTION_ROW,
} from "../config/styles";
import {
  ADMIN_TYPE,
  BREADCRUMB_HOME,
  BREADCRUMB_MERCHANTS,
  CREATE_PRODUCT_BUTTON,
  CREATE_CATEGORY_BUTTON,
  CREATE_USER_BUTTON,
  BREADCRUMB_PRODUCT,
  BREADCRUMB_CATEGORY,
} from "../config/constants";
import { ROUTES } from "../config/routes";

const CustomBreadcrumbStyled = styled("div")(({ theme, showBackButton }) => ({
  "& .custom-header": {
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_ROW,
    justifyContent: FLEX_JUSTIFY_SPACE_BETWEEN,
    alignItems: FLEX_ALIGN_CENTER,
    padding: "36px 40px 24px 40px",
    marginLeft: showBackButton ? "288px" : "256px",
    backgroundColor: theme.palette.secondary.contrastText,
    borderRadius: "12px 0px",
  },
  "& .breadcrumb-item a": {
    textDecoration: TEXT_DECORATION_NONE,
  },
  "& .back-button": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "28px",
    fontWeight: 500,
    lineHeight: "36px",
    color: colors.custom,
    padding: 0,
    marginLeft: "-35px",
    "&:hover": {
      opacity: 0.8,
    },
  },
  "& .button-container": {
    width: "168px",
    height: "44px",
    marginRight: "36px",
    padding: "8px 12px",
    display: DISPLAY_FLEX,
    justifyContent: FLEX_ALIGN_CENTER,
    alignItems: FLEX_ALIGN_CENTER,
    gap: ".5rem",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
    },
    "& span": {
      marginLeft: "4px",
    },
  },
}));

export const CustomBreadcrumb = ({
  header,
  currentPage,
  children,
  isCreateUser = false,
  onCreateUser = () => {},
  isFromLandingPage = false,
  isFromMarketplace = false,
  showBackButton = false,
  onBackClick = null,
  rightContent = null,
  parentPage = null,
  parentPageHref = null,
}) => {
  const role = localStorage.getItem("role");
  const configShow =
    role === ADMIN_TYPE.SUPER_ADMIN || role === ADMIN_TYPE.SALES_ADMIN;
  const schoolName = configShow
    ? localStorage.getItem("superAdminSchoolName")
    : localStorage.getItem("schoolAdminSchoolName");
  const createProductButton = header === BREADCRUMB_CATEGORY ? CREATE_CATEGORY_BUTTON : CREATE_PRODUCT_BUTTON;  
  const productButton = header === BREADCRUMB_PRODUCT ? BREADCRUMB_PRODUCT : BREADCRUMB_CATEGORY; 
  const hrefLinkForMarket = header === BREADCRUMB_PRODUCT ? ROUTES.MARKETPLACE : ROUTES.CATEGORIES;
  return (
    <CustomBreadcrumbStyled showBackButton={showBackButton}>
      <div className="custom-header">
        <div className="d-flex flex-column gap-1 w-100">
          <div className="d-flex justify-content-between align-items-center w-100">
            {showBackButton ? (
              <button 
                className="back-button"
                onClick={onBackClick}
              >
                <BackButtonLogo />
               <Typography variant="body2">{header}</Typography>
              </button>
            ) : (
              <Typography variant="body2">{header}</Typography>
            )}
            {rightContent}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Breadcrumb>
              <Breadcrumb.Item
                href={
                  isFromMarketplace
                    ? ROUTES.MARKETPLACE
                    : ROUTES.MAIN_LANDING_PAGE
                }
                bsPrefix="breadcrumb-item"
              >
                <Typography variant="h4" component="span">
                  {isFromMarketplace ? BREADCRUMB_MERCHANTS : BREADCRUMB_HOME}
                </Typography>
              </Breadcrumb.Item>
              
              {/* School/Merchant Name - Second Level */}
              {!isFromLandingPage && !isFromMarketplace && schoolName && (
                <Breadcrumb.Item
                  href={ROUTES.STUDENT_DATA}
                  bsPrefix="breadcrumb-item"
                >
                  <Typography variant="h4" component="span">
                    {schoolName}
                  </Typography>
                </Breadcrumb.Item>
              )}
              
              {/* Marketplace Product/Category - Second Level */}
              {isFromMarketplace && (
                <Breadcrumb.Item
                  href={hrefLinkForMarket}
                  bsPrefix="breadcrumb-item"
                >
                  <Typography variant="h4" component="span">
                    {productButton}
                  </Typography>
                </Breadcrumb.Item>
              )}
              
              {/* Parent Page - Third Level (e.g., Smart plans) */}
              {parentPage && parentPageHref && (
                <Breadcrumb.Item
                  href={parentPageHref}
                  bsPrefix="breadcrumb-item"
                >
                  <Typography variant="h4" component="span">
                    {parentPage}
                  </Typography>
                </Breadcrumb.Item>
              )}

              {/* Current Page - Last Level */}
              <Breadcrumb.Item href="#" bsPrefix="breadcrumb-item">
                <Typography
                  variant="h4"
                  component="span"
                  sx={{ color: colors.custom }}
                >
                  {currentPage}
                </Typography>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
        {isCreateUser ? (
          <Button className="button-container" onClick={onCreateUser}>
            <Typography variant="body1" sx={{ color: colors.white }}>
              <SideBarAddLogo bgColor={colors.white} />
              <span>
                {isFromMarketplace ? createProductButton : CREATE_USER_BUTTON}
              </span>
            </Typography>
          </Button>
        ) : null}
      </div>
      {children}
    </CustomBreadcrumbStyled>
  );
};
CustomBreadcrumb.propTypes = {
  header: PropTypes.string.isRequired,
  currentPage: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isCreateUser: PropTypes.bool,
  onCreateUser: PropTypes.func,
  isFromLandingPage: PropTypes.bool,
  isFromMarketplace: PropTypes.bool,
  showBackButton: PropTypes.bool,
  onBackClick: PropTypes.func,
  rightContent: PropTypes.node,
  parentPage: PropTypes.string,
  parentPageHref: PropTypes.string,
};
